import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, get, set, setProperties } from '@ember/object';
import { all, task, dropTask } from 'ember-concurrency';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import normalizeResponse from 'garaje/utils/normalize-response';
import { service } from '@ember/service';
import urlBuilder from 'garaje/utils/url-builder';
import {
  getUnixTime,
  fromUnixTime,
  startOfDay,
  endOfDay,
  formatISO,
  format,
  subMinutes,
  isSameDay,
  isAfter,
} from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { getPartialTimes } from 'garaje/utils/hour-options';
import { isPresent } from '@ember/utils';
import { RESERVATION_MODAL_PAGES } from 'garaje/utils/enums';
import { createURN } from 'garaje/models/generic-resource';
import { ResourceType } from 'garaje/services/live-map';
import { GENERIC_RESOURCE_ID_MAPPING } from 'garaje/utils/enums';

/**
 * @param {Array}       employees
 * @param {Task}        searchTask
 * @param {Task}        loadMoreTask
 * @param {Task}        reservationModalTask
 * @param {Boolean}     hasMorePages
 * @param {Boolean}     isEditing
 * @param {Reservation} reservation
 */

export default class ReservationModal extends Component {
  @service state;
  @service store;
  @service flashMessages;
  @service ajax;
  @service metrics;
  @service workplaceMetrics;
  @service featureFlags;
  @service currentAdmin;
  @service liveMap;

  @tracked currentPage = 'employee';
  @tracked selectedEmployee;
  @tracked selectedDates = this.initialSelectedDates;
  @tracked selectedDesks = [];
  @tracked selectedResourceFeatures = [];
  @tracked successfulReservations = [];
  @tracked reservationForSelectedEmployee;
  @tracked multidayEnabled = false;
  @tracked allDayEnabled = this.args.reservation?.isPartialDay ? false : true;
  @tracked selectedTime = this.initialSelectedTime;
  @tracked autoAssignEnabled = false;
  @tracked selectedResourceType = this.initialSelectedResourceType;

  get initialSelectedDates() {
    const { reservation, entry } = this.args;
    // Resource reservation have an iso string for the start and end time, but desk reservations have unix time
    const isValidUnixTime = fromUnixTime(reservation?.startTime).toString() !== 'Invalid Date';
    const startTime = isValidUnixTime ? fromUnixTime(reservation?.startTime) : new Date(reservation?.startTime);
    const startDate = reservation ? startTime : (entry?.expectedArrivalTime ?? new Date());
    return [
      this.state.getOfficeLocationTime(
        startOfDay(subMinutes(startDate, this.state.minutesBetweenTimezones(startDate))),
      ),
    ];
  }

  get initialSelectedTime() {
    const { isEditing, reservation } = this.args;
    if (!isEditing || !reservation) {
      return { startTime: '00:00:00', endTime: '23:59:59' };
    }

    // Resource reservation have an iso string for the start and end time, but desk reservations have unix time
    const isValidUnixTime = fromUnixTime(reservation.startTime).toString() !== 'Invalid Date';

    const start = isValidUnixTime ? fromUnixTime(reservation.startTime) : new Date(reservation.startTime);
    const end = isValidUnixTime ? fromUnixTime(reservation.endTime) : new Date(reservation.endTime);
    return {
      startTime: format(subMinutes(start, this.state.minutesBetweenTimezones(start)), 'HH:mm:ss'),
      endTime: format(subMinutes(end, this.state.minutesBetweenTimezones(end)), 'HH:mm:ss'),
    };
  }

  get initialSelectedResourceType() {
    const { reservation } = this.args;
    return reservation?.resourceTypeId
      ? { name: 'Parking', code: 'parking', resourceType: ResourceType.PARKING }
      : { name: 'Desk', code: 'desk', resourceType: ResourceType.DESK };
  }

  @task
  *loadInvitesTask(date) {
    try {
      const endTime = this.getLocationEndTime(date);
      const filter = {
        locationId: get(this.state.currentLocation, 'id'),
        dateTo: encodeURIComponent(formatISO(endTime)),
        dateFrom: encodeURIComponent(formatISO(date)),
        email: encodeURIComponent(this.selectedEmployee.email),
      };
      const url = urlBuilder.v3.invites.fetchEmployeeInviteOnDate(filter);
      const response = yield this.ajax.request(url, {
        type: 'GET',
        contentType: 'application/vnd.api+json',
        headers: { accept: 'application/vnd.api+json' },
      });

      const activeInvite = response.data.find((invite) => !invite.attributes['entry-signed-out-at']);
      if (activeInvite) {
        let invite = this.store.findRecord('invite', activeInvite.id);
        if (!invite) {
          const normalizedInvite = normalizeResponse(this.store, 'invite', { data: activeInvite });
          invite = this.store.createRecord('invite', normalizedInvite.data);
          setProperties(invite, {
            ...normalizedInvite.data.attributes,
          });
        }
        return invite;
      } else {
        return null;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('error:', e);
    }
  }

  @task
  *createInviteTask(date) {
    const startTime = date;
    const endTime = this.getLocationEndTime(date);
    const invite = yield this.state.currentLocation.createEmployeeInvite(
      startTime,
      this.selectedEmployee.fullName,
      this.selectedEmployee.email,
    );
    invite.endTime = endTime;
    const inviteUserDatum = invite.userData[0];
    // The create invite functions in the location model assign the flow name, userdata defining the flow, and flow rel for the first valid flow it locates.
    // We want to remove the rel relating to a specific flow by id and let the BE determine the proper flow based off the flow-name and userdata.
    invite.flow = null;
    invite.employeeScreeningFlow = true;
    invite.flowName = 'Employee registration';
    inviteUserDatum.value = 'Employee registration';

    yield invite.save();
    return invite;
  }

  @task
  *loadOrCreateInviteTask(date) {
    let invite = yield this.loadInvitesTask.perform(date);
    if (!invite) {
      invite = yield this.createInviteTask.perform(date);
    }
    return invite;
  }

  getReservationObj(user, location, invite, desk, startTime, endTime) {
    const resObj = {
      data: {
        type: 'reservation',
        attributes: {
          'start-time': startTime,
          'end-time': endTime,
        },
        meta: {
          'auto-assign-desk': this.autoAssignEnabled,
        },
        relationships: {
          user: {
            data: {
              type: 'users',
              id: user.id,
            },
          },
          location: {
            data: {
              type: 'locations',
              id: location.id,
            },
          },
          invite: {
            data: {
              type: 'invites',
              id: invite.id,
            },
          },
        },
      },
    };
    if (desk) {
      resObj.data.relationships.desk = {
        data: {
          type: 'desks',
          id: desk.id,
        },
      };
    }
    return resObj;
  }

  getLocationEndTime(date) {
    return this.state.getOfficeLocationTime(endOfDay(toZonedTime(date, this.state.currentLocation.timezone)));
  }

  createDeskReservationTask = task({ drop: true }, async () => {
    const { isEditing, reservation, entry, isDbeam } = this.args;
    if (isEditing && !reservation && !entry) {
      this.workplaceMetrics.logMonitorError({
        event: 'RESERVATION_MODAL_EDITING_NONEXISTENT_RESERVATION',
      });
      this.flashMessages.showAndHideFlash('Error editing reservation', "The reservation doesn't exist!");
      return;
    }

    try {
      if (isEditing && reservation) {
        await this._editDeskReservation(reservation);
      }
      if (isEditing && entry) {
        await entry.destroyRecord();
      }
      if (!isEditing) {
        await this._createDeskReservation();
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('error', e);
      this.workplaceMetrics.logMonitorError({
        event: 'RESERVATION_MODAL_FAILED_TO_CREATE_RESERVATION',
        debugExtras: {
          isDbeam,
          entryId: entry?.id,
          reservationId: reservation?.id,
          action: isEditing ? 'edit' : 'create',
        },
        error: e,
      });
      this.flashMessages.showAndHideFlash('Error creating reservation', parseErrorForDisplay(e));
      return;
    }

    this.setCurrentPage('approval');
  });

  _createDeskReservation = async () => {
    const { isDbeam } = this.args;
    const invites = await all(this.selectedDates.map((date) => this.loadOrCreateInviteTask.perform(date)));

    const shouldSkipReservationCreation = isPresent(isDbeam) && isDbeam && !this.autoAssignEnabled;
    if (shouldSkipReservationCreation) {
      return;
    }

    const reservationObjects = [];
    const user = await this.selectedEmployee.user;
    this.selectedDates.forEach((date, idx) => {
      const invite = invites[idx];
      const [partialStart, partialEnd] = getPartialTimes(date, this.selectedTime);
      const startTime = this.allDayEnabled ? date : partialStart;
      const endTime = this.allDayEnabled ? this.getLocationEndTime(date) : partialEnd;

      const createdReservation = this.getReservationObj(
        user,
        this.state.currentLocation,
        invite,
        this.selectedDesks[idx],
        getUnixTime(startTime),
        getUnixTime(endTime),
      );
      reservationObjects.push(createdReservation);
    });

    const url = urlBuilder.rms.bulkCreateReservations();
    const reservations = await this.ajax.request(url, {
      type: 'POST',
      contentType: 'application/vnd.api+json',
      data: reservationObjects,
    });

    this.store.pushPayload(reservations);

    reservations.data.forEach((res) => {
      const desk = this.store.peekRecord('desk', res.relationships.desk.data.id);
      const reservation = this.store.peekRecord('reservation', res.id);
      this.successfulReservations.push({
        startTime: fromUnixTime(reservation.startTime),
        endTime: fromUnixTime(reservation.endTime),
        desk,
      });
      this.metrics.trackEvent('DelegatedBooking_Used', {
        action: 'create',
        reservationId: res.id,
        partialDay: !this.allDayEnabled,
        multiday: this.multidayEnabled,
      });
    });

    if (reservations.errors) {
      this.workplaceMetrics.logMonitorError({
        event: 'RESERVATION_MODAL_FAILED_TO_CREATE_RESERVATIONS_MULTIDAY',
        debugExtras: {
          isDbeam,
          action: 'create',
        },
        error: reservations.errors,
      });

      this.flashMessages.showAndHideFlash(
        'error',
        'Reservation not successful',
        'One or more of your reservations failed',
      );
    }
  };

  _editDeskReservation = async (reservation) => {
    const locationTime = this.state.getOfficeLocationTime(
      startOfDay(subMinutes(this.selectedDates[0], this.state.minutesBetweenTimezones(this.selectedDates[0]))),
    );
    const invite = await this.loadOrCreateInviteTask.perform(locationTime);

    const [partialStart, partialEnd] = getPartialTimes(locationTime, this.selectedTime);
    const startTime = this.allDayEnabled ? locationTime : partialStart;
    const endTime = this.allDayEnabled ? this.getLocationEndTime(locationTime) : partialEnd;
    const newDesk = this.selectedDesks.length ? this.selectedDesks[0] : get(reservation, 'desk');
    const response = await reservation.editReservation(invite, newDesk, getUnixTime(startTime), getUnixTime(endTime));
    const newReservation = response.data;

    this.successfulReservations = [
      {
        startTime: fromUnixTime(newReservation.attributes['start-time']),
        endTime: fromUnixTime(newReservation.attributes['end-time']),
        desk: newDesk,
      },
    ];

    this.metrics.trackEvent('DelegatedBooking_Used', {
      action: 'edit',
      prevReservationId: reservation.id,
      reservationId: newReservation.id,
      partialDay: !this.allDayEnabled,
      multiday: this.multidayEnabled,
    });
  };

  get resourceTypeId() {
    return GENERIC_RESOURCE_ID_MAPPING[this.selectedResourceType.code];
  }

  createResourceReservationTask = task({ drop: true }, async () => {
    const { isDbeam, isEditing } = this.args;
    if (isDbeam) {
      await all(this.selectedDates.map((date) => this.loadOrCreateInviteTask.perform(date)));
    }

    const shouldSkipReservationCreation = isPresent(isDbeam) && isDbeam && !this.autoAssignEnabled;
    if (shouldSkipReservationCreation) {
      this.setCurrentPage('approval');
      return;
    }
    let reservations;
    if (isEditing) {
      reservations = await this._editResourceReservation();
    } else {
      reservations = await this._createResourceReservation();
    }

    // This is needed to load resources and floors into the store. This is needed if auto assigned is enabled
    await this.liveMap.fetchAndSaveLiveMapDataTask.perform({ selectedTime: getUnixTime(this.selectedDates[0]) });

    this.successfulReservations = reservations;
    this.flashMessages.showFlash('success', 'Reservations successfully created');
    this.setCurrentPage('approval');
  });

  _editResourceReservation = async () => {
    const { reservation } = this.args;
    this.metrics.trackEvent('Resource reservation edited', {
      resourceType: this.selectedResourceType.code,
      isDbeam: this.args.isDbeam,
    });
    const locationTime = this.state.getOfficeLocationTime(
      startOfDay(subMinutes(this.selectedDates[0], this.state.minutesBetweenTimezones(this.selectedDates[0]))),
    );
    const changeReservationUrl = urlBuilder.rms.changeResourceReservation(reservation.reservationId);
    const payload = {
      startTime: locationTime,
      endTime: this.getLocationEndTime(locationTime),
      resourceId: this.selectedResourceFeatures.length
        ? this.selectedResourceFeatures[0].externalId
        : reservation.resourceId,
      resourceTypeId: this.resourceTypeId,
      bookingSource: 'web_app',
    };

    const response = await this.ajax.request(changeReservationUrl, {
      type: 'POST',
      contentType: 'application/vnd.api+json',
      data: { data: payload },
    });
    return [response];
  };

  _createResourceReservation = async () => {
    const reservationPromises = [];
    this.metrics.trackEvent('Resource reservations created', {
      numberOfReservations: this.selectedDates.length,
      resourceType: this.selectedResourceType.code,
      isDbeam: this.args.isDbeam,
    });
    for (let i = 0; i < this.selectedDates.length; i++) {
      const date = this.selectedDates[i];
      const locationTime = this.state.getOfficeLocationTime(
        startOfDay(subMinutes(date || new Date(), this.state.minutesBetweenTimezones(date || new Date()))),
      );
      const locationEndTime = this.state.getOfficeLocationTime(
        endOfDay(toZonedTime(date || new Date(), this.state.currentLocation.timezone)),
      );

      const resourceFeature = this.selectedResourceFeatures[i];
      const payload = {
        relationId: createURN('location', this.state.currentLocation.id),
        startTime: locationTime?.toISOString(),
        endTime: locationEndTime?.toISOString(),
        reservedFor: createURN('employee', this.selectedEmployee.id),
        reservedBy: createURN('employee', this.currentAdmin.id),
        bookingSource: 'web_app',
      };

      if (resourceFeature && resourceFeature.externalId) {
        payload.resourceId = resourceFeature.externalId;
      } else {
        payload.resourceTypeId = this.resourceTypeId;
      }

      const url = urlBuilder.rms.resourceReservations();
      const reservationPromise = this.ajax.request(url, {
        type: 'POST',
        contentType: 'application/vnd.api+json',
        data: { data: payload },
      });
      reservationPromises.push(reservationPromise);
    }
    try {
      const reservations = await all(reservationPromises);
      return reservations;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('error', e);
    }
  };

  get successfulReservationsNormalized() {
    return this.successfulReservations.map((reservation) => {
      if (this.selectedResourceType.code === 'desk') {
        return {
          // eslint-disable-next-line ember/use-ember-get-and-set
          resourceName: reservation.desk.get('name'),
          type: 'desk',
          // eslint-disable-next-line ember/use-ember-get-and-set
          floorName: reservation.desk.get('floor').get('name'),
          startTime: reservation.startTime,
          endTime: reservation.endTime,
        };
      } else {
        const resource = this.store.peekRecord('generic-resource', reservation.data.resourceId);
        const mapFloor = this.store.peekRecord('map-floor', resource.floorId);

        return {
          resourceName: resource.name,
          type: this.selectedResourceType.code,
          floorName: mapFloor.name,
          startTime: reservation.data.startTime,
          endTime: reservation.data.endTime,
        };
      }
    });
  }

  deleteReservationTask = task({ drop: true }, async () => {
    if (this.selectedResourceType.code === 'desk') {
      await this.deleteDeskReservationTask.perform();
    } else {
      await this.deleteResourceReservationTask.perform();
    }
  });

  deleteResourceReservationTask = task({ drop: true }, async () => {
    const { reservation, reservationModalTask, isDbeam } = this.args;
    const url = urlBuilder.rms.deleteResourceReservation(reservation.reservationId);
    await this.ajax.request(url, {
      type: 'DELETE',
      contentType: 'application/vnd.api+json',
    });
    this.metrics.trackEvent('Resource reservation deleted', {
      resourceType: this.selectedResourceType.code,
      reservationId: reservation.reservationId,
      isDbeam: isDbeam,
    });
    this.flashMessages.showAndHideFlash('success', 'Reservation deleted');
    reservationModalTask.last.continue(this.selectedResourceType.code);
  });

  deleteDeskReservationTask = dropTask(async () => {
    const { reservation, reservationModalTask, entry, isDbeam } = this.args;
    const reservationId = reservation?.id ?? entry?.hasMany('reservations').ids()[0];

    try {
      if (entry) {
        if (!entry.signedInAt) {
          await entry.destroyRecord();
        } else {
          set(entry, 'signOutTime', new Date());
          await entry.save();
        }
      }

      const url = urlBuilder.rms.releaseDesk();

      if (reservationId) {
        await this.ajax.request(url, {
          type: 'POST',
          contentType: 'application/vnd.api+json',
          data: {
            data: {
              attributes: {
                'reservation-id': reservationId,
              },
            },
          },
        });
        if (reservation) this.store.unloadRecord(reservation);

        this.metrics.trackEvent('DelegatedBooking_Used', {
          action: 'delete',
          reservationId,
          partialDay: !this.allDayEnabled,
        });
      }
    } catch (e) {
      this.workplaceMetrics.logMonitorError({
        event: 'RESERVATION_MODAL_FAILED_TO_DELETE_RESERVATION',
        debugExtras: {
          isDbeam,
          action: 'delete',
          entryId: entry?.id,
          reservationId,
        },
        error: e,
      });

      throw e;
    }

    reservationModalTask.last.continue(this.selectedResourceType.code);
  });

  getSelectedEmployeeTask = task({ drop: true }, async () => {
    const { isEditing, reservation, entry } = this.args;
    if (!isEditing) {
      this.selectedDates = [this.state.getOfficeLocationTime(startOfDay(new Date()))];
      return;
    }

    const isValidUnixTime = fromUnixTime(reservation?.startTime).toString() !== 'Invalid Date';
    const startDate = isValidUnixTime ? fromUnixTime(reservation?.startTime) : new Date(reservation?.startTime);

    const startTime = reservation ? startDate : (entry?.expectedArrivalTime ?? new Date());
    this.selectedDates = [
      this.state.getOfficeLocationTime(subMinutes(startTime, this.state.minutesBetweenTimezones(startTime))),
    ];

    let params;
    if (reservation) {
      const user = await reservation.user;
      const email = user ? user.email : reservation.reservedForProfileData.email;
      params = { filter: { email } };
    } else {
      params = { filter: { email: entry.email } };
    }

    const response = await this.store.query('employee', params);
    this.selectedEmployee = response.slice()[0];
    await this.selectedEmployee.employeeLocations;
  });

  get modalTitle() {
    const { isEditing } = this.args;
    switch (this.currentPage) {
      case 'employee':
        return isEditing ? 'Edit reservation' : 'New reservation';
      case 'map':
        return 'Choose a desk';
      case 'resources-map':
        if (this.selectedResourceType.code === 'desk') {
          return 'Choose a Desk';
        } else if (this.selectedResourceType.code === 'parking') {
          return 'Choose a Parking Spot';
        } else {
          return 'Choose a resource';
        }
      case 'delete':
        return 'Delete reservation?';
      case 'unsaved':
        return 'You have unsaved changes';
      default:
        return '';
    }
  }

  get hasUnsavedChanges() {
    // Checks if the date changed or a desk has been selected
    const { isEditing, reservation } = this.args;
    if (isEditing && reservation) {
      return (
        !isSameDay(startOfDay(fromUnixTime(reservation?.startTime)), startOfDay(this.selectedDates[0])) ||
        this.selectedDesks[0]
      );
    } else {
      return (
        this.selectedDates.length !== 1 ||
        !isSameDay(startOfDay(this.selectedDates[0]), this.state.getOfficeLocationTime(startOfDay(new Date()))) ||
        this.selectedDesks[0]
      );
    }
  }

  @action
  onDateSelect(locationDate) {
    if (this.multidayEnabled) {
      if (this.selectedDates.find((date) => isSameDay(date, new Date(locationDate)))) {
        this.selectedDates = this.selectedDates.filter((date) => !isSameDay(date, new Date(locationDate)));
      } else {
        this.selectedDates = [...this.selectedDates, new Date(locationDate)].sort((a, b) => {
          return isAfter(a, b) ? 1 : -1;
        });
      }
    } else {
      this.selectedDates = [new Date(locationDate)];
    }
  }

  @action
  onEmployeeSelect(employee) {
    this.selectedEmployee = employee;
  }

  @action
  setCurrentPage(page) {
    this.currentPage = page;
  }

  @action
  setSelectedResourceType(resourceType) {
    this.selectedResourceType = resourceType;
  }

  @action
  setSelectedDesk(desk, idx) {
    this.selectedDesks[idx] = desk;
    this.selectedDesks = [...this.selectedDesks];
  }

  @action
  setSelectedResourceFeatures(feature, idx) {
    this.selectedResourceFeatures[idx] = feature;
    this.selectedResourceFeatures = [...this.selectedResourceFeatures];
  }

  @action
  onMultidayToggleClick() {
    this.multidayEnabled = !this.multidayEnabled;
    this.selectedDates = this.selectedDates.length ? [this.selectedDates[0]] : [];
  }

  @action
  onAllDayToggleClick() {
    this.allDayEnabled = !this.allDayEnabled;
  }

  @action
  onPartialTimeChange(attr, value) {
    this.selectedTime = {
      ...this.selectedTime,
      [attr]: value,
    };
  }

  resetComponent() {
    const { isEditing, reservation, entry } = this.args;
    const originalTime =
      isEditing && reservation ? fromUnixTime(reservation?.startTime) : (entry?.expectedArrivalTime ?? new Date());
    this.selectedEmployee = null;
    this.selectedDates = [this.state.getOfficeLocationTime(startOfDay(originalTime))];
    this.selectedDesks = [];
    this.selectedResourceFeatures = [];
    this.successfulReservations = [];
    this.currentPage = 'employee';
    this.multidayEnabled = false;
    this.allDayEnabled = reservation?.isPartialDay ? false : true;
    this.autoAssignEnabled = false;
    this.selectedTime = this.initialSelectedTime;
  }

  @action
  exitModal() {
    const { reservationModalTask } = this.args;
    switch (this.currentPage) {
      case RESERVATION_MODAL_PAGES.EMPLOYEE:
      case RESERVATION_MODAL_PAGES.MAP:
        if (this.hasUnsavedChanges) {
          this.currentPage = 'unsaved';
        } else {
          this.resetComponent();
          reservationModalTask.last.abort();
        }
        break;
      case RESERVATION_MODAL_PAGES.RESOURCES_MAP:
        this.resetComponent();
        reservationModalTask.last.abort();
        break;
      case RESERVATION_MODAL_PAGES.DELETE:
        this.currentPage = RESERVATION_MODAL_PAGES.EMPLOYEE;
        break;
      case RESERVATION_MODAL_PAGES.UNSAVED:
        this.resetComponent();
        reservationModalTask.last.abort();
        break;
      case RESERVATION_MODAL_PAGES.APPROVAL:
        this.resetComponent();
        reservationModalTask.last.continue(this.selectedResourceType.code);
    }
  }
}
